import { z } from "zod";
import { Finite, IsoDate, IsoInstant, Symbol_ } from "./primitives";
import { PersonaCadence, TradeRecord, TradeReason } from "./persona";
import type { EquityPoint, PersonaPosition, PersonaSkip } from "./persona";
import { RiskFlag, Sleeve } from "./scoring";
import type { Weights } from "./scoring";
import type { FlagTolerance } from "./persona";

/*
 * The vocabulary of the shared simulation core.
 *
 * The persona portfolios and the virtual portfolios users run are the same
 * simulation with different owners, so they share one trading engine
 * (packages/engine/src/personas/trade.ts). What differs is the document each
 * one persists, which is why everything here describes the *shape the engine
 * touches* rather than either concrete document.
 *
 * The enums are widened here rather than in persona.ts on purpose. Both
 * PersonaCadence and TradeReason are consumed by exhaustive `Record<…>` label
 * maps in the web app, so adding a member in place turns into a typecheck
 * failure in a file that has nothing to do with the new feature. A superset
 * keeps every existing value assignable while leaving those maps complete.
 */

/** Every persona cadence, plus the one only a user portfolio can choose. */
export const SimCadence = z.enum([...PersonaCadence.options, "manual"] as const);
export type SimCadence = z.infer<typeof SimCadence>;

/**
 * The cadences a user portfolio may be set to: everything the nightly sweep
 * can act on. `intraday` is excluded because nothing ticks a user portfolio
 * during the session — a portfolio stored with it would sit idle forever
 * while claiming to trade.
 */
export const VpCadence = z.enum(["daily", "weekly", "deterioration", "manual"]);
export type VpCadence = z.infer<typeof VpCadence>;

/**
 * Every reason the rule engine emits, plus the ones only a user portfolio can
 * produce: their own hand-entered trades, and trades copied from a persona.
 */
export const SimTradeReason = z.enum([
  ...TradeReason.options,
  "MANUAL_BUY",
  "MANUAL_SELL",
  "MIRROR",
] as const);
export type SimTradeReason = z.infer<typeof SimTradeReason>;

/**
 * A trade record that can carry any of the wider reasons, plus the provenance
 * of its fill price.
 *
 * The provenance fields live here and not on `TradeRecord` because only the
 * interactive path can produce a live fill, and that path writes
 * `SimTradeRecord` exclusively — persona ledgers are written by the nightly
 * batch, which has nothing to stamp.
 *
 * `.optional()` rather than `.default("close")`: a default *materializes*, so
 * every legacy trade round-tripped through a parse would come back carrying a
 * claim its writer never made, silently rewriting history. And
 * `z.literal("live")` makes "close" unrepresentable, so absence is unambiguous.
 * The rule is: if it says live, believe it; otherwise it is the close.
 */
export const SimTradeRecord = TradeRecord.extend({
  reason: SimTradeReason,
  priceSource: z.literal("live").optional(),
  /** When the filling print happened. Present iff `priceSource` is set. */
  priceAsOf: IsoInstant.optional(),
});
export type SimTradeRecord = z.infer<typeof SimTradeRecord>;

/**
 * The part of a scored candidate the simulation actually reads.
 *
 * Four fields out of twelve. Stating that explicitly is what lets the API ship
 * a ~200 KB projection of the snapshot rather than parsing the full 2.5 MB
 * document on every cold start — for a trade request, that parse *is* the
 * latency.
 */
export interface SimCandidate {
  symbol: string;
  sleeves: Sleeve[];
  riskFlags: RiskFlag[];
  lastPrice: number | null;
}

/** One day's scored universe, as the simulation sees it. */
export interface SimUniverse {
  asOfDate: string;
  universeSize: number;
  candidates: readonly SimCandidate[];
}

/**
 * One owner's trading rules.
 *
 * Structural, not a zod schema, so both `PersonaDefinition` (engine config)
 * and a user portfolio's stored settings satisfy it without either being
 * converted into the other. `label` exists only to name the owner in error
 * messages; the engine never branches on it.
 */
export interface SimSettings {
  label: string;
  weights: Weights;
  positions: number;
  leverage: number;
  bankFloor: number;
  cadence: SimCadence;
  flagTolerance: FlagTolerance;
  /**
   * Buy indivisible shares rather than fractions of them.
   *
   * True for user portfolios, where the discrete unit is a real constraint a
   * person actually faces. False for the personas, which exist to measure the
   * scoring: equal-weighting a ranked list is the standard way to test a
   * ranking signal, and discreteness adds noise unrelated to the signal. Their
   * stored positions are fractional already.
   */
  wholeShares: boolean;
}

/**
 * Tuning shared by every simulated portfolio, persona or user.
 *
 * `startingCapital` is deliberately absent: it belongs to bootstrap, and a
 * running portfolio must never be able to consult it.
 */
export interface SimTuning {
  minTradeValue: number;
  missingGraceDays: number;
  thinSnapshotFloor: number;
  deterioration: { scoreDrop: number; rankFloorMultiple: number };
}

/**
 * The mutable state one trading day reads and rewrites.
 *
 * Any document with these fields can be traded, and the engine returns the
 * same object type it was given with only these fields replaced — so a user
 * portfolio's `userId`, `mode` and `strategy` survive a trade untouched. Note
 * what is *not* here: `weights`. The engine takes weights from the settings
 * argument, so the stored copy is a persona-only concern (see
 * `assertWeightsFrozen`).
 */
export interface SimPortfolioState {
  bank: number;
  lastTradedAsOf: string | null;
  lastRebalanceAsOf: string | null;
  positions: PersonaPosition[];
  realizedPnlTotal: number;
  equityHistory: EquityPoint[];
  months: string[];
  skips: PersonaSkip[];
  updatedAt: string;
}

/**
 * The snapshot projection the API ships beside the function.
 *
 * A trade request has to score the universe before it can act, and the full
 * snapshot is about 2.5 MB — parsing it on a cold start would be most of the
 * request's latency, for ten fields where four are used. This is the same
 * shape as `SimUniverse`, expressed as a schema so the file can be validated
 * on the way in like every other artifact.
 */
export const SimUniverseArtifact = z.object({
  asOfDate: IsoDate,
  universeSize: z.number().int().nonnegative(),
  candidates: z.array(
    z.object({
      symbol: Symbol_,
      sleeves: z.array(Sleeve),
      riskFlags: z.array(RiskFlag),
      lastPrice: Finite.positive().nullable(),
    }),
  ),
});
export type SimUniverseArtifact = z.infer<typeof SimUniverseArtifact>;
